import CONFIG from '../config';
import { isWeb } from '../environment-utils';
import { injectTags } from '../inject-tags';
import { logMessage } from '../sentry';
import { partnerInfos } from './partner-infos';
import { getDisplayTrackUrl } from '../TrackAnalyticsHelper';
import {
  setOmidTrackingEvent,
  EVENT_OMID_SDK_ACTIVATED,
  EVENT_OMID_DISPLAY_TRACKING
} from './omid-tracking';

const omidSessionClient: OmidSessionClient = window.OmidSessionClient;
let omSdk: OMSDK = omidSessionClient && omidSessionClient.default;

let adEvents: AdEvents;
let mediaEvents: MediaEvents;
let adSession: AdSession;
let adSessionContext: Context;
let shouldSkipNextEvents = false;
let impressionHasBeenSent = false;
let isCreativeLoaded = false;
let isSessionStarted = false;
let loadedVastProperties: VastProperties | null = null;
let pendingImpression = false;
let impressionDelay = 0;

const mediaEventNames = [
  'video',
  'loaded',
  'start',
  'firstQuartile',
  'midpoint',
  'thirdQuartile',
  'complete',
  'pause',
  'resume',
  'bufferStart',
  'bufferFinish',
  'skipped',
  'volumeChange',
  'playerStateChange',
  'adUserInteraction'
];

const IMPRESSION_TYPE = 'beginToRender';
const IMPRESSION_TYPE_MWEB = 'beginToRender';
const CREATIVE_DISPLAY_TYPE = 'htmlDisplay';
const CREATIVE_VIDEO_TYPE = 'video';

/**
 * Create a new AdSession
 */
function getAdSession() {
  // Omid SDK not loaded in the page
  if (!omSdk) {
    throw new Error('omSdk not found.');
  }

  if (!adSession) {
    throw new Error('adSession not found.');
  }

  return adSession;
}

export function isOMIDSDKActivated() {
  if (!window.OmidSessionClient || !window.OmidSessionClient.default) {
    return false;
  }
  const {
    AdSession,
    Context,
    Partner,
    VerificationScriptResource
  }: OMSDK = window.OmidSessionClient.default;

  if (!AdSession || !Context || !Partner || !VerificationScriptResource) {
    return false;
  }
  return true;
}

function createAdSession(
  javascriptResources: JavascriptResource[] = [],
  isVideo: boolean = false,
  htmlElement: HTMLElement,
  videoPlayer: HTMLVideoElement | null = null
) {
  // Omid SDK not loaded in the page
  if (!omSdk) {
    throw new Error('omSdk not found.');
  }

  if (adSession) {
    throw new Error('omSdk adSession already created.');
  }

  const {
    AdSession,
    Context,
    Partner,
    VerificationScriptResource
  }: OMSDK = omSdk;

  if (!AdSession || !Context || !Partner || !VerificationScriptResource) {
    throw new Error('omSdk does not have a proper implementation.');
  }

  const partner = new Partner(
    partnerInfos.name,
    window.MRAID_ENV?.sdkVersion || partnerInfos.version
  );

  const scriptResources = javascriptResources.map(
    jsResource =>
      new VerificationScriptResource(
        jsResource.resourceUrl,
        jsResource.vendorKey,
        jsResource.verificationParameters,
        'full' // accessMode
      )
  );

  adSessionContext = new Context(partner, scriptResources);
  adSessionContext.setSlotElement(htmlElement);

  if (isWeb()) {
    if (videoPlayer) {
      // In mWeb environment, we need to set the video element before the AdSession
      // Without this line we cannot get the viewability metadatas in the impression event
      adSessionContext.setVideoElement(videoPlayer);
    }

    adSessionContext.underEvaluation = true;
    adSessionContext.setServiceWindow(window);
  }

  adSession = new AdSession(adSessionContext);

  if (videoPlayer && !isWeb()) {
    // In In-app environment, we need to set the video element after the AdSession
    // This prevent the OMSDK to throw an error in the console
    adSessionContext.setVideoElement(videoPlayer);
  }

  if (!adSession.isSupported()) {
    throw new Error('omSdk adSession is not supported.');
  }

  const creativeType = isVideo ? CREATIVE_VIDEO_TYPE : CREATIVE_DISPLAY_TYPE;

  adSession.setCreativeType && adSession.setCreativeType(creativeType);
  if (isWeb()) adSession.setImpressionType(IMPRESSION_TYPE_MWEB);

  try {
    adSession.registerSessionObserver(event => {
      if (event.type === 'sessionStart') {
        isSessionStarted = true;

        if (event.data.creativeType === 'definedByJavaScript') {
          adSession.setCreativeType(creativeType);
        }

        if (event.data.impressionType === 'definedByJavaScript') {
          adSession.setImpressionType(IMPRESSION_TYPE);
        }

        // Dispatch any impression that was queued before the session started
        // Must happen before loaded() which could trigger side effects
        if (pendingImpression) {
          pendingImpression = false;
          sendOmidImpression();
        }

        if (isCreativeLoaded) {
          adEvents.loaded(loadedVastProperties);
        }
      }
    });
  } catch (error) {
    logMessage('Omid session register errored.');
  }

  if (isWeb()) {
    adSession.start();
  }

  return adSession;
}

interface InitOmidAdEventsOptions {
  omidTrackers?: string;
  javascriptResources?: JavascriptResource[];
  enableOgyOmidTag?: boolean;
  isVideo?: boolean;
  videoPlayer?: HTMLVideoElement | null;
  enableOmidViewabilityTracks?: boolean;
}
/**
 * Initialize OMID adSession with a new Partner and Context
 */
export function initOmidAdEvents(
  htmlElement: HTMLElement,
  {
    omidTrackers,
    javascriptResources = [],
    enableOgyOmidTag = true,
    isVideo = false,
    videoPlayer = null,
    enableOmidViewabilityTracks = false
  }: InitOmidAdEventsOptions = {}
) {
  setOmidTrackingEvent(EVENT_OMID_SDK_ACTIVATED, Boolean(omSdk));
  setOmidTrackingEvent(EVENT_OMID_DISPLAY_TRACKING, omidTrackers !== '');

  // Omid SDK not loaded in the page
  if (!omSdk) {
    return;
  }

  if (adEvents) {
    return;
  }

  if (!omSdk.AdEvents) {
    logMessage('omSdk does not have AdEvents field.');
    return;
  }

  try {
    injectOmidTrackers(omidTrackers);
    if (enableOgyOmidTag) {
      javascriptResources.push({
        resourceUrl: CONFIG.INHOUSE_OMID_URL,
        vendorKey: 'ogury',
        verificationParameters: enableOmidViewabilityTracks
          ? { trackUrl: getDisplayTrackUrl('[event]') }
          : {}
      });
    }

    const omAdSession = createAdSession(
      javascriptResources,
      isVideo,
      htmlElement,
      videoPlayer
    );
    adEvents = new omSdk.AdEvents(omAdSession);
  } catch (error) {
    logMessage('init Omid AdEvents failed.', {
      error
    });
  }
}

export function setVideoElementToContext(videoHtmlElement: HTMLVideoElement) {
  if (!omSdk || !adSessionContext) {
    return;
  }

  try {
    adSessionContext.setVideoElement(videoHtmlElement);
  } catch (error) {
    logMessage('setVideoElementToContext failed.', {
      error
    });
  }
}

/**
 * Initialize OMID Video
 */
export function initOmidMediaEvents() {
  if (!omSdk) {
    return;
  }

  if (mediaEvents) {
    return;
  }

  if (!omSdk.MediaEvents) {
    logMessage('omSdk does not have MediaEvents field.');
    return;
  }

  try {
    const adSessionRef = getAdSession();
    mediaEvents = new omSdk.MediaEvents(adSessionRef);
  } catch (error) {
    logMessage('init Omid MediaEvents failed', {
      error
    });
  }
}

/**
 * Send a video event to OMID SDK service
 */
export function sendOmidMediaEvent(
  eventType:
    | 'firstQuartile'
    | 'midpoint'
    | 'thirdQuartile'
    | 'complete'
    | 'pause'
    | 'resume'
    | 'bufferStart'
    | 'bufferFinish'
    | 'skipped'
): void;
export function sendOmidMediaEvent(
  eventType: 'start',
  duration: number,
  videoPlayerVolume: number
): void;
export function sendOmidMediaEvent(
  eventType: 'volumeChange',
  videoPlayerVolume: number
): void;
export function sendOmidMediaEvent(
  eventType: 'playerStateChange',
  playerState: VideoPlayerState
): void;
export function sendOmidMediaEvent(
  eventType: 'adUserInteraction',
  interactionType: InteractionType
): void;
export function sendOmidMediaEvent(
  eventType: OmidVideoEvent,
  ...options: any[]
): void {
  if (shouldSkipNextEvents) {
    return;
  }

  if (!mediaEvents) {
    return;
  }

  if (mediaEventNames.indexOf(eventType) === -1) {
    return;
  }

  const mediaEventFunction: (...options: any[]) => void =
    mediaEvents[eventType];
  if (typeof mediaEventFunction !== 'function') {
    logMessage('MediaEvents is not a function', {
      eventType
    });
    return;
  }

  try {
    // When the video has been clicked, no events should be fired.
    if (eventType === 'skipped') {
      sendOmidSessionFinish();
      shouldSkipNextEvents = true;
    }

    mediaEventFunction(...options);
  } catch (error) {
    logMessage(
      `send Omid MediaEvent : unable to call the method ${eventType}.`,
      {
        eventType,
        error
      }
    );
  }
}

/**
 * Send an event to OMID SDK service that an impression occurred
 */
export function sendOmidImpression() {
  if (!adEvents) {
    return;
  }

  // Queue if session not started yet
  if (!isSessionStarted) {
    pendingImpression = true;
    return;
  }

  const sendImpression = () => {
    if (impressionHasBeenSent) {
      return;
    }

    try {
      adEvents.impressionOccurred();
      impressionHasBeenSent = true;
    } catch (error) {
      if (Math.random() > 0.8) {
        logMessage('Omid Impression is not sent.', {
          error: JSON.stringify(error)
        });
      }
    }
  };

  if (impressionDelay === 0) {
    sendImpression();
  } else {
    setTimeout(sendImpression, impressionDelay);
  }
}

/**
 * Send an event to OMID SDK service that a loaded event occurred
 */
export function sendOmidLoaded(vastProperties: VastProperties | null = null) {
  if (!adEvents) {
    return;
  }

  try {
    if (vastProperties) {
      const { isSkippable, skipOffset, isAutoPlay, position } = vastProperties;

      loadedVastProperties = new omSdk.VastProperties(
        isSkippable,
        skipOffset,
        isAutoPlay,
        position
      );
    }

    if (isSessionStarted && !isCreativeLoaded) {
      adEvents.loaded(loadedVastProperties);
    }
  } catch (error) {
    logMessage('Omid Loaded is not sent.');
  }

  isCreativeLoaded = true;
}

/**
 * Only for MWEB
 * Send an event to OMID SDK service that finish the session
 */
export function sendOmidSessionFinish() {
  if (isWeb()) {
    if (!adSession) {
      return;
    }

    try {
      adSession.finish();
    } catch (error) {
      logMessage('Omid session finish is not sent.', {
        error
      });
    }
  }
}

function injectOmidTrackers(omidTrackers?: string) {
  const trackers: string[] = [];

  // Inject 3rd party's trackers
  if (omidTrackers) {
    trackers.push(omidTrackers);
  }

  if (trackers.length === 0) {
    return;
  }

  const injectInto = 'omid-tags';
  const injectIntoSelector = `#${injectInto}`;
  const container = document.createElement('div');
  container.id = injectInto;
  document.body.appendChild(container);

  injectTags(
    trackers.join(''),
    injectIntoSelector,
    injectIntoSelector,
    'body',
    {
      overloadDocumentWrite: false,
      enableSentryLog: false,
      allowInjectIntoBody: true
    }
  );
}

export function setOmidImpressionDelay(omidImpressionDelay = 0) {
  impressionDelay = omidImpressionDelay;
}

/* webpack-strip-block:removed */
